// oidc-client-ts surfaces the authorization server's OAuth error code on
// `ErrorResponse.error`, with `error_description` as the message. Keycloak
// answers a refresh whose refresh token has died with `invalid_grant` /
// "Token is not active". The rest are the standard OIDC codes that likewise
// say the session is gone.
const EXPIRED_SESSION_ERROR_CODES = [
  "invalid_grant",
  "invalid_token",
  "login_required",
  "interaction_required",
  "consent_required",
  "account_selection_required",
  "session_selection_required",
];

// Keycloak isn't always consistent about the code it pairs with a dead token.
const EXPIRED_SESSION_MESSAGES = [
  "token is not active",
  "session not active",
  "not_active",
];

const hasExpiredSessionCode = (error) => {
  let current = error;

  while (current && typeof current === "object") {
    if (EXPIRED_SESSION_ERROR_CODES.includes(current.error)) {
      return true;
    }

    const message = [current.message, current.error_description]
      .filter((value) => typeof value === "string")
      .join(" ")
      .toLowerCase();

    if (EXPIRED_SESSION_MESSAGES.some((phrase) => message.includes(phrase))) {
      return true;
    }

    current = current.innerError;
  }

  return false;
};

/**
 * Whether an error from `useAuth()` means the user has to log in again.
 *
 * A silent renew is the app's only way to stay logged in without the user, so
 * a renewal that failed — for any reason — leaves an interactive login as the
 * only way forward. That covers both halves of a tab left open overnight: the
 * refresh token Keycloak rejects, and the request that never reached Keycloak
 * because the network wasn't back yet. Neither is recoverable by trying again
 * with the same dead tokens.
 *
 * Errors from anywhere else are only re-authentication when they carry an
 * expired-session code, so a broken callback still reaches the error boundary
 * instead of bouncing the user into a login loop.
 *
 * @param {unknown} error - the `error` from `useAuth()`.
 * @returns {boolean}
 */
export const requiresReauthentication = (error) => {
  if (!error || typeof error !== "object") return false;

  return error.source === "renewSilent" || hasExpiredSessionCode(error);
};
